import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ShieldCheck, Sparkles, Activity, Hospital,
  Stethoscope, Pill, FlaskConical, Users, Brain, Zap, Globe,
  CheckCircle2, ArrowRight, Cpu, Lock, Database, MessageSquare,
  ClipboardList, CalendarCheck, ClipboardCheck, Rocket,
} from "lucide-react";

import HeartbeatLine from "@/components/HeartbeatLine";
import EcosystemGraph from "@/components/EcosystemGraph";
import HealthIDCard from "@/components/HealthIDCard";
import SectionHeader from "@/components/SectionHeader";
import PatientBanner from "@/components/PatientBanner";

import hosp1 from "../assets/hosp1.png";
import hosp2 from "../assets/hosp2.png";
import hosp3 from "../assets/hosp3.png";
import hosp4 from "../assets/hosp4.png";
import hosp5 from "../assets/hosp5.png";

const HOSPITALS = [
  { name: "ApolloVita", logo: hosp1, site: "https://www.apollovita.com" },
  { name: "MediCore", logo: hosp2, site: "https://www.medicore.com" },
  { name: "Helix Diagnostics", logo: hosp3, site: "https://www.helixdiagnostics.com" },
  { name: "CarePoint", logo: hosp4, site: "https://www.carepoint.com" },
  { name: "Lotus Hospitals", logo: hosp5, site: "https://www.lotushospitals.com" },
  { name: "NeoLab", logo: hosp1, site: "https://www.neolab.com" },
  { name: "SunPharma", logo: hosp2, site: "https://www.sunpharma.com" },
  { name: "MaxWell Clinics", logo: hosp3, site: "https://www.maxwellclinics.com" },
];

const HERO_BG = "https://images.unsplash.com/photo-1655635643486-a17bc48771ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwdGVjaG5vbG9neSUyMGFic3RyYWN0fGVufDB8fHx8MTc4MDc1NjIzOHww&ixlib=rb-4.1.0&q=85";
const DOCTOR_IMG = "https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FACILITY_IMG = "https://images.pexels.com/photos/13176356/pexels-photo-13176356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const DraggableMarquee = ({ items }) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const isInteracting = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let animationFrameId;

    setTimeout(() => {
      if (el) el.scrollLeft = el.scrollWidth / 4;
    }, 100);

    const step = () => {
      if (!isInteracting.current) {
        el.scrollLeft += 1;
      }
      const singleSetWidth = el.scrollWidth / 4;
      if (el.scrollLeft >= singleSetWidth * 2) {
        el.scrollLeft -= singleSetWidth;
      } else if (el.scrollLeft <= singleSetWidth * 0.5) {
        el.scrollLeft += singleSetWidth;
      }
      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const onMouseDown = (e) => {
    isInteracting.current = true;
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const onMouseMove = (e) => {
    if (!isInteracting.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    if (Math.abs(walk) > 10) {
      setIsDragging(true);
    }
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const onMouseUp = () => {
    isInteracting.current = false;
    setTimeout(() => setIsDragging(false), 50);
  };

  return (
    <div className="marquee">
      <div
        ref={scrollRef}
        className="flex overflow-x-auto no-scrollbar cursor-grab active:cursor-grabbing w-full"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={() => (isInteracting.current = true)}
        onTouchEnd={() => {
          isInteracting.current = false;
          setTimeout(() => setIsDragging(false), 50);
        }}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className={`flex w-max py-4 ${isDragging ? 'pointer-events-none' : ''}`}>
          {[...items, ...items, ...items, ...items].map((h, i) => (
            <a
              key={i}
              href={h.site}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 opacity-60 px-14 flex-shrink-0 hover:opacity-100 transition-opacity select-none"
              draggable="false"
            >
              <img
                src={h.logo}
                alt={h.name}
                draggable="false"
                className="h-16 md:h-24 lg:h-28 w-auto object-contain filter grayscale select-none pointer-events-none"
              />
              <span className="whitespace-nowrap select-none text-slate-700 text-2xl font-bold uppercase tracking-tight">{h.name}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[color:var(--paper-2)]" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid-light opacity-50" />
        <div
          className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full blur-[140px] opacity-40"
          style={{ background: "radial-gradient(closest-side, #FFE9E2, transparent)" }}
        />


        <div className="relative container-x py-4 md:py-6 lg:py-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 chip">
                <Sparkles className="w-3.5 h-3.5" />
                AI Healthcare Super Platform · v2.0
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="h-hero text-[2.6rem] sm:text-6xl lg:text-[5.2rem] mt-5"
              data-testid="hero-headline"
            >
              Connecting <span className="text-[color:var(--brand)]">Healthcare.</span>
              <br />
              <span className="brand-underline">Empowering Lives.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl font-medium"
            >
              One unified, AI-driven SaaS ecosystem for hospitals, clinics, pharmacies, labs, doctors and patients —
              go live in 48 hours*, Scale across every branch, and own a single Health ID for every life you touch.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="mt-9 flex flex-wrap gap-3 items-center"
            >
              {/*
              <Link to="/request-demo" className="btn-primary" data-testid="hero-cta-demo">
                Request Free Demo <ArrowUpRight className="w-4 h-4" />
              </Link>
              */}
              <Link to="/ai-suite" className="btn-ghost" data-testid="hero-cta-ai">
                Explore AI Suite <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs text-slate-500">
              {[
                ["48hr*", "Go-live"],
                ["12+", "Modules"],
                ["99.95%", "Uptime SLA"],
                // ["HIPAA", "Compliant"],
              ].map(([n, l]) => (
                <div key={l} className="flex items-baseline gap-2">
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit',sans-serif" }}>{n}</div>
                  <div className="uppercase tracking-[0.15em] font-semibold">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center">
            <PatientBanner />
          </div>
        </div>

        {/* heartbeat divider */}
        <div className="relative">
          <HeartbeatLine height={80} />
        </div>
      </section>

      {/* ===================== TRUST MARQUEE (COMMENTED OUT) ===================== */}
      {/*
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="container-x">
          <div className="text-center text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-6">
            Powering healthcare networks · clinics · multi-specialty hospitals · diagnostic chains
          </div>
          <DraggableMarquee items={HOSPITALS} />
        </div>
      </section>
      */}

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="pt-20 md:pt-28 pb-8 md:pb-12 bg-[color:var(--paper-2)]" data-testid="how-section">
        <div className="container-x">
          <SectionHeader
            eyebrow="How Saaluvars Works"
            title="From paper chaos to a"
            titleAccent="connected ecosystem — in 4 steps."
            subtitle="No long IT projects. No migrations from hell. We onboard your organisation, configure your modules and go live within 48 hours."
          />

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ClipboardCheck, n: "01", t: "Discover", d: "30-min walkthrough. We map your workflows and the modules you actually need." },
              { icon: ClipboardList, n: "02", t: "Configure", d: "Branches, roles, GST, taxes, doctors, labs, pharmacy — all set up for you." },
              { icon: CalendarCheck, n: "03", t: "Train & Migrate", d: "Hands-on team training. Bulk-import patients, inventory and prescriptions." },
              { icon: Rocket, n: "04", t: "Go Live", d: "Live within 48 hours* with 24×7 support and a dedicated success manager." },
            ].map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                viewport={{ once: true }}
                className="group relative rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="absolute top-4 right-5 text-5xl font-bold text-slate-100 group-hover:text-[color:var(--brand)] transition-colors duration-300" style={{ fontFamily: "'Outfit',sans-serif" }}>{s.n}</div>
                <div className="w-12 h-12 rounded-xl bg-[color:var(--brand-soft)] grid place-items-center text-[color:var(--brand)]">
                  <s.icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 text-xl font-bold">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== HEALTH ID ===================== */}
      <section className="pt-8 md:pt-12 pb-20 md:pb-28 bg-white relative overflow-hidden" data-testid="health-id-section">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <HealthIDCard />
          </div>
          <div>
            <SectionHeader
              eyebrow="Saaluvars Health ID"
              title="One identity."
              titleAccent="Every record. Anywhere."
              subtitle="A lifetime, portable health passport for every patient. Carry your prescriptions, lab reports, vitals and history across any hospital, clinic or pharmacy on the SAALUVARS network — securely."
            />
            <ul className="mt-8 space-y-4">
              {[
                ["Lifetime Medical Records", "Every consult, prescription, lab and bill stitched to one ID."],
                ["Cross-Hospital Access", "Switch providers without losing a single data point."],
                ["Patient-Owned & Consent-First", "Patients approve every record share with one tap."],
                ["AI Health Insights", "Personalised recommendations from longitudinal data."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4" data-testid={`health-id-feature-${i + 1}`}>
                  <CheckCircle2 className="w-5 h-5 text-[color:var(--brand)] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-slate-900">{t}</div>
                    <div className="text-sm text-slate-600">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/apps#patient" className="btn-dark" data-testid="health-id-explore">
                Explore Patient App <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== ECOSYSTEM (DARK) ===================== */}
      <section className="section-y relative overflow-hidden bg-[#0B1220] text-white" data-testid="ecosystem-section">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[color:var(--brand)] blur-[180px] opacity-20" />

        <div className="relative container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeader
              dark
              eyebrow="The Unified Ecosystem"
              title="Every healthcare role,"
              titleAccent="under one platform."
              subtitle="Hospitals, pharmacies and labs run their core operations. Doctors, nurses, pharmacists, lab technicians and patients work through dedicated apps. SAALUVARS connects them all in real time."
            />
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { icon: Hospital, t: "HMS" },
                { icon: Pill, t: "Pharmacy" },
                { icon: FlaskConical, t: "Laboratory" },
                { icon: Stethoscope, t: "Doctor App" },
                { icon: Users, t: "Nurse App" },
                { icon: Activity, t: "Patient App" },
                { icon: Brain, t: "AI Suite" },
                { icon: ShieldCheck, t: "Insurance" },
              ].map((m, i) => (
                <div
                  key={m.t}
                  className="flex items-center gap-3 rounded-xl glass-dark px-4 py-3"
                  data-testid={`eco-module-${i + 1}`}
                >
                  <m.icon className="w-5 h-5 text-cyan-300" />
                  <span className="font-medium text-sm">{m.t}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/solutions" className="btn-primary" data-testid="ecosystem-cta">
                See all Solutions <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div>
            <EcosystemGraph />
          </div>
        </div>
      </section>

      {/* ===================== AI HIGHLIGHT (COMMENTED OUT) ===================== */}
      {/*
      <section className="section-y bg-white" data-testid="ai-highlight-section">
        <div className="container-x">
          <SectionHeader
            eyebrow="AI Healthcare Suite"
            title="The first AI workforce"
            titleAccent="built for healthcare."
            subtitle="Receptionist that schedules, scribe that documents, prescription assistant that reviews drug interactions, follow-up bot that engages — all natively trained on healthcare workflows."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageSquare, t: "AI Receptionist", d: "24×7 WhatsApp + voice bot for bookings, reminders, FAQs." },
              { icon: ClipboardList, t: "AI Medical Scribe", d: "Auto-generates SOAP notes from doctor-patient conversation." },
              { icon: Pill, t: "AI Prescription Assistant", d: "Detects drug interactions, dosage conflicts, allergies in real time." },
              { icon: CalendarCheck, t: "AI Follow-Up", d: "Personalised post-discharge engagement & adherence nudges." },
              { icon: Activity, t: "AI Analytics", d: "Predictive demand, no-show alerts, revenue intelligence." },
              { icon: Brain, t: "AI Triage", d: "Symptom checker that routes patients to the right specialist." },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                viewport={{ once: true }}
                className="group relative rounded-2xl border border-slate-200 bg-white p-6 hover:border-[color:var(--brand)] hover:shadow-xl transition-all"
                data-testid={`ai-card-${i + 1}`}
              >
                <div className="w-11 h-11 rounded-xl grid place-items-center bg-slate-900 text-white group-hover:bg-[color:var(--brand)] transition-colors">
                  <c.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 text-lg font-bold">{c.t}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{c.d}</p>
                <ArrowUpRight className="absolute top-6 right-6 w-4 h-4 text-slate-300 group-hover:text-[color:var(--brand)] transition" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* ===================== VISUAL STRIP (image + ecg) ===================== */}
      <section className="relative bg-[color:var(--paper-2)] overflow-hidden" data-testid="visual-strip">
        <div className="container-x py-20 grid lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 relative rounded-3xl overflow-hidden min-h-[360px] group">
            <img src={DOCTOR_IMG} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/85 via-slate-900/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10 text-white">
              <div className="eyebrow text-cyan-300">For Healthcare Providers</div>
              <h3 className="mt-3 text-3xl md:text-4xl font-bold max-w-md">Run your entire hospital from a single login.</h3>
              <p className="mt-3 text-slate-200 max-w-md">Patients, OPD, IPD, billing, pharmacy, radiology, lab and insurance — connected. Forever.</p>
              <Link to="/solutions" className="btn-primary mt-5 w-fit">Explore HMS <ArrowUpRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-3xl bg-slate-900 text-white relative overflow-hidden p-8 md:p-10 flex flex-col">
            <div className="absolute inset-0 bg-grid-dark opacity-30" />
            <div className="relative">
              <div className="eyebrow text-[color:var(--brand)]">For Patients</div>
              <h3 className="mt-3 text-3xl md:text-4xl font-bold">Your health, in your pocket.</h3>
              <p className="mt-3 text-slate-300">Book appointments, talk to doctors, refill prescriptions, store every report — for life.</p>
            </div>
            <div className="relative mt-auto pt-8">
              <HeartbeatLine color="#22D3EE" height={56} />
              <Link to="/apps#patient" className="btn-ghost !bg-white !text-slate-900 mt-4">Open Patient App <ArrowUpRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== INTEGRATIONS / TRUST ===================== */}
      <section className="section-y bg-white" data-testid="integrations-section">
        <div className="container-x">
          <SectionHeader
            align="center"
            eyebrow="Integrations & Compliance"
            title="Plugs into the tools your team"
            titleAccent="already loves."
            subtitle="WhatsApp, Razorpay, Stripe, Tally — ready out of the box."
          />
          <div className="mt-12 flex flex-wrap justify-center gap-3 md:gap-4">
            {["WhatsApp", "Razorpay", "Stripe", "Tally", /*"ABDM",*/ "Google"].map((t) => (
              <div
                key={t}
                className="w-28 sm:w-36 md:w-44 aspect-[2/1] rounded-xl border border-slate-200 bg-white grid place-items-center font-semibold text-slate-700 hover:border-[color:var(--brand)] hover:-translate-y-0.5 hover:shadow transition-all text-sm"
              >
                {t}
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto gap-4">
            {[
              // { icon: Lock, t: "HIPAA · GDPR · ABDM compliant" },
              { icon: Database, t: "End-to-end encryption + audit trails" },
              { icon: Globe, t: "Cloud-native · India + UAE + EU regions" },
            ].map((b) => (
              <div key={b.t} className="flex items-center gap-3 rounded-2xl bg-[color:var(--paper-2)] border border-slate-200 p-5">
                <b.icon className="w-5 h-5 text-[color:var(--brand)]" />
                <div className="font-semibold text-sm">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="section-y bg-[color:var(--paper-2)]" data-testid="testimonials-section">
        <div className="container-x">
          <SectionHeader
            eyebrow="From the field"
            title="Built with"
            titleAccent="real clinicians, used daily."
          />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { q: "Our OPD waiting time dropped 38% in the first month. The AI receptionist alone paid for itself.", n: "Dr. Karunakaran S", r: "M.B.B.S., M.S. (ORTHO), D.N.B. (ORTHO)\nFOUNDER & SPINE SURGEON" },
              { q: "Finally a single dashboard for billing, pharmacy.", n: "Mr. Sadhu", r: "BPT • SENIOR PHYSIOTHERAPIST" },
              { q: "Combining advanced physiotherapy technologies with evidence-based care allows us to deliver better clinical outcomes and long-term recovery.", n: "Ms. Sushma", r: "BPT • PHYSIOTHERAPIST" },
            ].map((t, i) => (
              <div key={i} className="flex flex-col h-full rounded-2xl bg-white p-7 border border-slate-200" data-testid={`testimonial-${i + 1}`}>
                <Cpu className="w-5 h-5 text-[color:var(--brand)] shrink-0" />
                <p className="mt-4 mb-6 text-slate-800 leading-relaxed flex-1">&ldquo;{t.q}&rdquo;</p>
                <div className="pt-4 border-t border-slate-100">
                  <div className="font-semibold text-slate-900">{t.n}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5 whitespace-pre-line leading-relaxed">{t.r}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative section-y bg-white" data-testid="final-cta">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] text-white p-10 md:p-16">
            <img src={FACILITY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0F172A] via-[#0F172A]/85 to-[#0F172A]/40" />
            <div className="relative grid md:grid-cols-2 gap-8 items-end">
              <div>
                <div className="eyebrow text-cyan-300">Let&apos;s build the future of healthcare</div>
                <h2 className="mt-4 text-4xl md:text-6xl font-bold leading-[1.02]" style={{ fontFamily: "'Outfit',sans-serif" }}>
                  Your <span className="text-[color:var(--brand)]">unfair advantage</span> starts with one demo.
                </h2>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                {/*
                <Link to="/request-demo" className="btn-primary" data-testid="final-cta-demo">Request Demo <ArrowUpRight className="w-4 h-4" /></Link>
                */}
                <Link to="/pricing" className="btn-ghost !bg-transparent !text-white !border-white/30 hover:!border-white">See Pricing <Zap className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
